import React from 'react';
import { FIELD } from '../../components/ui/tokens';
import DateInput from "../../components/DateInput";

/**
 * The choices above a statement: which currency (only when the supplier has several), and the
 * period. Screen only; the printed statement states them in its own heading.
 *
 * An empty date box means "use the default" (the 90 days up to today), and it shows what the
 * server resolved, so the person can see the period they are reading.
 */
export interface StatementControlsProps {
  currencies: string[];
  /** The chosen currency; blank when none is chosen yet. */
  currency: string;
  /** The supplier has several currencies and none is chosen. */
  needsCurrency: boolean;
  onCurrencyChange: (currency: string) => void;
  from: string;
  to: string;
  onFromChange: (day: string) => void;
  onToChange: (day: string) => void;
  /** Plain-language reason the dates cannot be used, or ''. */
  rangeProblem: string;
  disabled?: boolean;
}

const LABEL = 'block text-xs font-medium text-slate-600 mb-1';

export const StatementControls: React.FC<StatementControlsProps> = ({
  currencies,
  currency,
  needsCurrency,
  onCurrencyChange,
  from,
  to,
  onFromChange,
  onToChange,
  rangeProblem,
  disabled = false,
}) => (
  <div className="no-print flex flex-wrap items-end gap-x-4 gap-y-3">
    {currencies.length > 1 ? (
      <div>
        <label htmlFor="statement-currency" className={LABEL}>
          Currency
        </label>
        <select
          id="statement-currency"
          value={currency}
          onChange={(e) => onCurrencyChange(e.target.value)}
          className={`${FIELD} h-9 min-w-[9rem] border-slate-300 bg-white`}
        >
          {needsCurrency || !currency ? (
            <option value="" disabled>
              Choose a currency
            </option>
          ) : null}
          {currencies.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>
    ) : null}

    <div>
      <label htmlFor="statement-from" className={LABEL}>
        From
      </label>
      <DateInput validity="filter"
        id="statement-from"
        value={from}
        onChange={(v) => onFromChange(v)}
        disabled={disabled}
        aria-invalid={rangeProblem ? true : undefined}
        aria-describedby={rangeProblem ? 'statement-range-problem' : undefined}
        className={`${FIELD} h-9 border-slate-300 bg-white`}
      />
    </div>
    <div>
      <label htmlFor="statement-to" className={LABEL}>
        To
      </label>
      <DateInput validity="filter"
        id="statement-to"
        value={to}
        onChange={(v) => onToChange(v)}
        disabled={disabled}
        aria-invalid={rangeProblem ? true : undefined}
        aria-describedby={rangeProblem ? 'statement-range-problem' : undefined}
        className={`${FIELD} h-9 border-slate-300 bg-white`}
      />
    </div>

    {rangeProblem ? (
      <p id="statement-range-problem" role="alert" className="basis-full text-xs font-medium text-red-700">
        {rangeProblem}
      </p>
    ) : null}
  </div>
);
